import { useEffect, useRef } from "react";

// Site-wide motion: sections and cards rise into view as the visitor scrolls, cards carry a soft
// glow that follows the pointer, and a thin bar tracks reading progress. Content is only hidden
// once JavaScript has marked it, so it stays visible without JS or with reduced motion.
const SECTIONS = "main > section:not(:first-child):not([data-reveal])";
const ITEMS = [
  ".use-case-card", ".related-card", ".specialist-card", ".detail-benefit-card", ".detail-workflow-card",
  ".capability-card", ".agent-cap", ".area-card", ".feature-box", ".lifecycle-stage", ".integration-group", ".automation-task", ".cost-section",
].join(",");
export const GLOW_CARDS = ".agent-cap, .use-case-card, .related-card, .specialist-card, .detail-benefit-card, .detail-workflow-card, .area-card, .feature-box";
const STAGGER_MS = 70;
const MAX_STAGGER = 6;
const SETTLE_MS = 900;

const prefersReducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export function useSiteMotion(routeKey) {
  useEffect(() => {
    if (prefersReducedMotion() || !("IntersectionObserver" in window)) return undefined;
    const timers = [];
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const element = entry.target;
        element.classList.add("is-in");
        observer.unobserve(element);
        // Once it has arrived, hand the element back to its own styles (hover effects, transforms).
        const delay = parseFloat(element.style.getPropertyValue("--motion-delay")) || 0;
        timers.push(window.setTimeout(() => element.classList.remove("motion-section", "motion-item", "is-in"), SETTLE_MS + delay));
      });
    }, { threshold: 0.08, rootMargin: "0px 0px -40px 0px" });

    // Let the route render before looking for what to animate.
    const frame = window.requestAnimationFrame(() => {
      document.querySelectorAll(SECTIONS).forEach((section) => {
        section.classList.add("motion-section");
        observer.observe(section);
      });
      // Cards in the same row arrive one after another.
      const siblings = new Map();
      document.querySelectorAll(ITEMS).forEach((item) => {
        const index = siblings.get(item.parentElement) || 0;
        siblings.set(item.parentElement, index + 1);
        item.style.setProperty("--motion-delay", `${Math.min(index, MAX_STAGGER) * STAGGER_MS}ms`);
        item.classList.add("motion-item");
        observer.observe(item);
      });
    });
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [routeKey]);

  useEffect(() => {
    if (prefersReducedMotion() || !window.matchMedia("(hover: hover)").matches) return undefined;
    const onMove = (event) => {
      const card = event.target.closest?.(GLOW_CARDS);
      if (!card) return;
      const box = card.getBoundingClientRect();
      card.style.setProperty("--mx", `${event.clientX - box.left}px`);
      card.style.setProperty("--my", `${event.clientY - box.top}px`);
    };
    document.addEventListener("pointermove", onMove, { passive: true });
    return () => document.removeEventListener("pointermove", onMove);
  }, []);
}

export function ScrollProgress() {
  const bar = useRef(null);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (bar.current) bar.current.style.transform = `scaleX(${max > 0 ? Math.min(window.scrollY / max, 1) : 0})`;
    };
    const onScroll = () => { if (!frame) frame = window.requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
  return <div className="scroll-progress" ref={bar} aria-hidden="true" />;
}
